import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppIntersectionObserver from '../app/appIntersectionObserver';
import AppLoading from '../app/appLoading';
import { useEmployeesSummary } from '../../hooks/reports/useEmployeesSummary';
import { AppNoFeedback } from '../app/appNoFeedback';
import {
  Box,
  Button,
  Theme,
  useTheme,
  Typography,
  IconButton,
  Tab,
  Tabs,
  Dialog,
} from '@mui/material';
import {
  EmployeesSummary,
  ReportFilters,
  ViewMode,
} from '../../models/reports';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { AppRow } from '../../layout/appRow';
import { ReportsEmployeeItemMobile } from './reportsEmployeeItemMobile';
import WestIcon from '@mui/icons-material/West';
import { ReportsInputsData } from './reportsInputsData';
import { ReportsEmployeeMobileDetails } from './reportsEmployeeMobileDetails';
import { MyCoolGradient } from '../app/button/myCoolGradient';
import { SmallLayoutRouter } from './reportsPageSmallLayout';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import { EmployeeOption, ReportsTeamLeadSelect } from './reportsTeamLeadSelect';
import { ROLES_ID } from '../../models/employee';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { setEmployeeCardState } from '../../store/reports/reportsSlice';
import { useAppDispatch } from '../../hooks';
import { ActivityFormData } from '../activity/activityForm';

interface Props {
  filters: ReportFilters;
  viewMode: ViewMode;
  viewUnloggedEmployees: boolean;
  viewShowInactiveEmployees: boolean;
  setRoute: (arg: SmallLayoutRouter) => void;
}

const makeStyles = (theme: Theme) => ({
  topBox: { display: 'flex', flexDirection: 'column', width: '100%' },

  allocationsRow: {
    mb: 2,
  },
  allocationsBtn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
  },
  backBtnContent: { display: 'flex', alignItems: 'center', fontSize: 16 },
  mobileItemsRow: { display: 'flex', flexDirection: 'column' },
  mobileViewsBox: { pt: 2, pr: 1, pl: 1 },
  backIcon: {
    mr: 2,
    fill: `url(#my-cool-gradient) ${theme.palette.primary.main}`,
  },
  tabContainer: {
    minHeight: '36px',
  },
  tabEdit: {
    '&.Mui-selected': {
      backgroundColor: '#80C1C3',
      borderRadius: '8px 8px 0 0',
      color: '#332E38',
    },
    '&.MuiTab-textColorPrimary': {
      color: '#332E38',
      textTransform: 'none',
      minHeight: '36px',
    },
  },
  addTabBtn: {
    color: ColorPalette.DARK_GRAY,
  },
  tabsMobileContainer: {
    borderBottom: 1,
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
  },
});

export function ReportsEmployeeListMobile({
  filters,
  viewUnloggedEmployees,
  viewShowInactiveEmployees,
  setRoute,
  viewMode,
}: Props): JSX.Element {
  const theme = useTheme();
  const styles = makeStyles(theme);

  const dispatch = useAppDispatch();

  const [skip, setSkip] = useState(0);
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeesSummary | null>(null);

  const [tabs, setTabs] = useState<EmployeeOption[]>([]);
  const [selectedTL, setSelectedTL] = useState<EmployeeOption>();
  const [teamLeads, setTeamLeads] = useState<EmployeesSummary[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isSelectTeamLead, setIsSelectTeamLead] = useState(false);

  const { items, isLoading, fetchData, isEmpty, hasMore, fetchEmployeeData } =
    useEmployeesSummary();

  useEffect(() => {
    if (filters.managerId && filters.managerName) {
      const initialPayload = {
        label: filters.managerName,
        id: filters.managerId,
      };
      setSelectedTL(initialPayload);
      setTabs([{ id: filters.managerId, label: 'Direct Reports' }]);
    }

    fetchData(filters, viewUnloggedEmployees, viewShowInactiveEmployees);
  }, [filters, fetchData, viewUnloggedEmployees, viewShowInactiveEmployees]);

  const initialTL = useMemo<EmployeeOption | null>(
    () =>
      filters.managerId && filters.managerName
        ? { label: filters.managerName, id: filters.managerId }
        : null,
    [filters.managerId, filters.managerName]
  );

  const handleObserver = useCallback(() => {
    if (isLoading || !hasMore) {
      return;
    }

    fetchData(
      {
        ...filters,
        managerId: selectedTL ? selectedTL.id : filters.managerId,
        managerName: selectedTL ? selectedTL.label : filters.managerName,
        skip: skip + DEFAULT_PAGE_SIZE,
      },
      viewUnloggedEmployees,
      viewShowInactiveEmployees
    );
    setSkip(skip + DEFAULT_PAGE_SIZE);
  }, [
    isLoading,
    hasMore,
    fetchData,
    filters,
    selectedTL,
    skip,
    viewUnloggedEmployees,
    viewShowInactiveEmployees,
  ]);

  const loadEmployeeTab = useCallback(
    (employee: EmployeeOption) => {
      if (!filters || !employee) {
        return;
      }
      setSelectedTL(employee);
      fetchData(
        {
          ...filters,
          managerId: employee.id,
          managerName: employee.label,
          skip: 0,
        },
        viewUnloggedEmployees,
        viewShowInactiveEmployees
      );
      setSkip(0);
    },
    [filters, fetchData, viewUnloggedEmployees, viewShowInactiveEmployees]
  );

  const addEmployeeTab = useCallback(
    (employee: EmployeeOption) => {
      const updatedTabs = [...tabs, { label: employee.label, id: employee.id }];
      setTabs(updatedTabs);
      setActiveTabIndex(updatedTabs.length - 1);

      loadEmployeeTab(employee);
    },
    [tabs, loadEmployeeTab]
  );

  const handleChangeTab = useCallback(
    (event, newTabIndex: number) => {
      setActiveTabIndex(newTabIndex);
      if (newTabIndex === 0 && initialTL) {
        loadEmployeeTab(initialTL);
      } else {
        loadEmployeeTab(tabs[newTabIndex]);
      }
    },
    [loadEmployeeTab, tabs, initialTL]
  );

  useEffect(() => {
    if (activeTabIndex === 0) {
      const leads = items.filter((item) =>
        item.employee.permissionGroups.some(
          (group) => group.id === ROLES_ID.TEAM_LEAD
        )
      );
      setTeamLeads(leads);
    }
  }, [items, activeTabIndex]);

  const handleOpenSelectTeamLead = useCallback(() => {
    setIsSelectTeamLead((prev) => !prev);
  }, []);

  const displayAddTab = useMemo(
    () =>
      filters &&
      teamLeads &&
      teamLeads.length > 0 &&
      filters.managerId &&
      filters.managerName,
    [teamLeads, filters]
  );

  const handleCloseTab = useCallback(
    (indexToRemove, event) => {
      event.stopPropagation();

      const tabsUpdated = [...tabs];
      tabsUpdated.splice(indexToRemove, 1);
      setTabs(tabsUpdated);

      if (indexToRemove === activeTabIndex) {
        const currentTabIndex = indexToRemove - 1;
        setActiveTabIndex(currentTabIndex);

        if (currentTabIndex === 0 && initialTL) {
          loadEmployeeTab(initialTL);
        } else {
          loadEmployeeTab(tabsUpdated[currentTabIndex]);
        }
      }

      if (indexToRemove < activeTabIndex) {
        setActiveTabIndex((prev) => prev - 1);
      }
    },
    [tabs, loadEmployeeTab, activeTabIndex, initialTL]
  );

  const reloadEmployeeData = useCallback(
    (
      employeeId: string,
      employeeName: string,
      lastLoggedActivity: ActivityFormData | null
    ) => {
      if (!filters) return;

      dispatch(setEmployeeCardState({ employeeId, lastLoggedActivity }));
      fetchEmployeeData(
        { ...filters, employeeId, employeeName },
        viewUnloggedEmployees,
        viewShowInactiveEmployees
      );
    },
    [
      filters,
      dispatch,
      fetchEmployeeData,
      viewUnloggedEmployees,
      viewShowInactiveEmployees,
    ]
  );

  useEffect(() => {
    if (selectedEmployee) {
      const foundEmployee = items.find(
        (emp) => emp.employee.id === selectedEmployee.employee.id
      );
      if (foundEmployee) {
        setSelectedEmployee(foundEmployee);
      }
    }
  }, [items]);

  return (
    <Box sx={styles.topBox}>
      <Box sx={styles.mobileViewsBox}>
        {!selectedEmployee ? (
          <>
            <AppRow sx={styles.allocationsRow}>
              <Button onClick={() => setRoute(SmallLayoutRouter.FILTERS)}>
                <Typography variant="h2" sx={styles.backBtnContent}>
                  <MyCoolGradient />
                  <WestIcon sx={styles.backIcon} />
                  <u style={styles.allocationsBtn}>Back</u>
                </Typography>
              </Button>
            </AppRow>
            <ReportsInputsData filters={filters} viewMode={viewMode} />

            {filters && (
              <Box sx={styles.tabsMobileContainer}>
                <Tabs
                  value={activeTabIndex}
                  onChange={handleChangeTab}
                  sx={styles.tabContainer}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  {tabs.map((tab, index) => (
                    <Tab
                      key={tab.label}
                      label={tab.label}
                      sx={styles.tabEdit}
                      icon={
                        index !== 0 ? (
                          <Box
                            onClick={(event) => handleCloseTab(index, event)}
                            style={{
                              cursor: 'pointer',
                              marginTop: '4px',
                              marginLeft: '8px',
                            }}
                          >
                            <CloseIcon
                              style={{ fontSize: 12 }}
                              color="action"
                            />
                          </Box>
                        ) : undefined
                      }
                      iconPosition="end"
                    />
                  ))}
                </Tabs>
                {displayAddTab && (
                  <IconButton
                    sx={styles.addTabBtn}
                    style={{ marginLeft: 4 }}
                    onClick={handleOpenSelectTeamLead}
                  >
                    <AddIcon style={{ fontSize: 20 }} />
                  </IconButton>
                )}
              </Box>
            )}
            <AppRow sx={styles.mobileItemsRow}>
              {items.map((item) => {
                return (
                  <ReportsEmployeeItemMobile
                    key={item.employee.id}
                    employeeSummary={item}
                    projectFilter={filters.projectId}
                    setSelectedEmployee={setSelectedEmployee}
                  />
                );
              })}
            </AppRow>
            <AppIntersectionObserver intersect={handleObserver} />
          </>
        ) : (
          <ReportsEmployeeMobileDetails
            filters={filters}
            reloadEmployeeData={reloadEmployeeData}
            selectedEmployee={selectedEmployee}
            setRoute={setRoute}
            setSelectedEmployee={setSelectedEmployee}
          />
        )}
      </Box>

      {isLoading && <AppLoading />}
      {isEmpty && <AppNoFeedback info="data" />}

      <Dialog open={isSelectTeamLead} sx={{ borderRadius: '5px' }}>
        <AppDialogTitle
          title="Pick a Team Lead"
          handleClose={handleOpenSelectTeamLead}
        />
        <ReportsTeamLeadSelect
          handleClose={handleOpenSelectTeamLead}
          managerId={initialTL?.id}
          handleDisplay={addEmployeeTab}
        />
      </Dialog>
    </Box>
  );
}
